import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, COMPANY } from "@/lib/api";
import { Reveal, SplitLines } from "@/components/ui-custom/Reveal";
import { EASE } from "@/lib/motion";

const TYPES = ["Independent supermarket", "Grocer / fruit & veg", "Deli / providore", "Cafe", "Restaurant", "Foodservice / catering", "Other"];

export default function Stockist() {
  const [params] = useSearchParams();
  const brandSlug = params.get("brand");
  const [brand, setBrand] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", business_type: "", location: "", interests: [], message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    if (brandSlug) api.get(`/brands/${brandSlug}`).then((r) => setBrand(r.data)).catch(() => {});
  }, [brandSlug]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (slug) => setForm({ ...form, interests: form.interests.includes(slug) ? form.interests.filter((s) => s !== slug) : [...form.interests, slug] });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, brand_slug: brandSlug || null });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-[72px]" data-testid="stockist-page">
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-16 lg:pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="eyebrow text-[var(--terracotta)] mb-6">Become a stockist</p>
          <SplitLines lines={["Let's grow your", "gourmet range."]} delay={0.1} className="font-display font-normal text-4xl sm:text-5xl lg:text-7xl leading-[1] text-[var(--ink)]" data-testid="stockist-title" />
          <Reveal delay={0.4}>
            <p className="mt-6 text-base md:text-lg font-light text-[var(--charcoal)] leading-relaxed">We're excited to partner with new stockists. Tell us about your store and what you're interested in, our team will come back with pricing, range lists and delivery days for your area.</p>
            {brand && (
              <div className="mt-8 flex items-center gap-4 border border-[var(--sand)] rounded-sm p-4 bg-white" data-testid="stockist-brand-context">
                {brand.logo_url && <img src={brand.logo_url} alt="" className="h-8 w-auto max-w-[100px] object-contain" />}
                <p className="text-sm text-[var(--charcoal)]">Enquiring about <span className="font-medium text-[var(--ink)]">{brand.name}</span></p>
              </div>
            )}
            <div className="mt-12 space-y-3 text-sm text-[var(--muted)]">
              <p>Prefer to talk? Call <a href={COMPANY.phoneHref} className="text-[var(--ink)] link-underline">{COMPANY.phone}</a>, {COMPANY.hours}.</p>
              <p>Or email <a href={`mailto:${COMPANY.email}`} className="text-[var(--ink)] link-underline">{COMPANY.email}</a></p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="bg-[var(--ink)] text-[var(--cream)] rounded-sm p-10 lg:p-14" data-testid="stockist-success">
                <span className="h-12 w-12 rounded-full bg-[var(--terracotta)] grid place-items-center"><Check size={22} /></span>
                <h2 className="font-display font-normal text-3xl lg:text-5xl mt-8 leading-[1.05]">Thank you, {form.name.split(" ")[0]}. We've received your enquiry.</h2>
                <p className="mt-5 text-[var(--stone)] font-light text-base md:text-lg">A member of the Deliver team will be in touch shortly. In the meantime, keep exploring the range.</p>
                <Link to="/brands" className="btn-pill btn-terracotta mt-10">Browse brands <ArrowUpRight size={14} /></Link>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }} className="space-y-8" data-testid="stockist-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                  <input required data-testid="stockist-name" className="input-line" placeholder="Your name *" value={form.name} onChange={set("name")} />
                  <input required type="email" data-testid="stockist-email" className="input-line" placeholder="Email *" value={form.email} onChange={set("email")} />
                  <input required data-testid="stockist-company" className="input-line" placeholder="Business name *" value={form.company} onChange={set("company")} />
                  <input data-testid="stockist-phone" className="input-line" placeholder="Phone" value={form.phone} onChange={set("phone")} />
                  <select data-testid="stockist-business-type" className="input-line bg-transparent" value={form.business_type} onChange={set("business_type")}>
                    <option value="">Type of business</option>
                    {TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input data-testid="stockist-location" className="input-line" placeholder="Suburb / postcode" value={form.location} onChange={set("location")} />
                </div>
                <div>
                  <p className="eyebrow text-[var(--muted)] mb-4">Which products are you most interested in?</p>
                  <div className="flex flex-wrap gap-2" data-testid="stockist-interests">
                    {categories.map((c) => (
                      <button type="button" key={c.slug} onClick={() => toggle(c.slug)} data-testid={`interest-${c.slug}`} className={`chip ${form.interests.includes(c.slug) ? "active" : ""}`}>{c.name}</button>
                    ))}
                  </div>
                </div>
                <textarea data-testid="stockist-message" rows={4} className="input-line resize-none" placeholder="Anything else we should know? Current suppliers, volumes, delivery days…" value={form.message} onChange={set("message")} />
                <button type="submit" disabled={sending} data-testid="stockist-submit" className="btn-pill btn-ink disabled:opacity-60">
                  {sending ? <><Loader2 size={14} className="animate-spin" /> Sending</> : <>Send enquiry <ArrowUpRight size={14} /></>}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
}
